import React from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {motion} from 'framer-motion';

const {FiPlay, FiUsers, FiClock, FiCreditCard, FiArrowRight, FiZap, FiCheck} = FiIcons;

const NewHero = () => {
  const handleCTAClick = () => {
    window.open('https://register.yiksy.com/', '_blank');
  };

  const handleVideoClick = () => {
    window.open('https://www.youtube.com/watch?v=nFVsQ8Dp5gs', '_blank');
  };

  return (
    <section className="bg-white pt-24 sm:pt-32 lg:pt-36 pb-16 sm:pb-20 lg:pb-24 overflow-hidden relative">
      {/* Abstract background elements */}
      <div className="absolute top-0 right-0 w-[300px] sm:w-[400px] lg:w-[600px] h-[300px] sm:h-[400px] lg:h-[600px] bg-blue-50 rounded-full mix-blend-multiply filter blur-3xl opacity-40 translate-x-1/4 -translate-y-1/4"></div>
      <div className="absolute bottom-0 left-0 w-[200px] sm:w-[300px] lg:w-[400px] h-[200px] sm:h-[300px] lg:h-[400px] bg-purple-50 rounded-full mix-blend-multiply filter blur-3xl opacity-30 -translate-x-1/4 translate-y-1/4"></div>
      <div className="absolute top-1/2 left-1/2 w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] bg-blue-50/30 rounded-full mix-blend-multiply filter blur-3xl opacity-20 -translate-x-1/2 -translate-y-1/2"></div>
      
      {/* Noise texture overlay */}
      <div className="absolute inset-0 bg-noise-pattern opacity-20"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-4xl mx-auto mb-12 sm:mb-16">
          <motion.div 
            className="inline-flex items-center bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6 sm:mb-8 backdrop-blur-sm"
            initial={{opacity: 0, y: -20}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6}}
          >
            <SafeIcon icon={FiUsers} className="mr-2 text-sm" />
            <span className="text-xs sm:text-sm">Exclusive Invite-Only Private Beta</span>
          </motion.div>
          
          <motion.h1 
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-text mb-6 sm:mb-8 leading-tight tracking-tight px-2"
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.2}}
          >
            Create a <span className="text-primary">Month</span> of{' '}
            <br className="hidden sm:block" />
            Social Content in <span className="text-primary">Minutes</span>
          </motion.h1>
          
          <motion.p 
            className="text-lg sm:text-xl lg:text-2xl text-text-light mb-8 sm:mb-10 leading-relaxed px-4 sm:px-8"
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.4}}
          >
            Your AI-powered content partner. We set up your workspace with your brand's voice and design so you can create, customize, and post across socials in just a few clicks.
          </motion.p>
          
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-center justify-center mb-8 sm:mb-10 px-4"
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.6}}
          >
            <motion.button 
              className="bg-primary text-white px-8 sm:px-10 py-4 sm:py-5 rounded-xl text-lg font-bold hover:bg-blue-700 transition-all transform hover:scale-105 shadow-xl flex items-center group neon-glow w-full sm:w-auto justify-center touch-manipulation"
              whileHover={{scale: 1.05}}
              whileTap={{scale: 0.98}}
              onClick={handleCTAClick}
            >
              Build My Free Profile
              <SafeIcon icon={FiArrowRight} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </motion.button>
            
            <div className="flex items-center text-text-light text-sm sm:text-base">
              <div className="flex items-center mr-4 sm:mr-6">
                <SafeIcon icon={FiClock} className="mr-2 text-primary/70" />
                <span>15 days free</span>
              </div>
              <div className="flex items-center">
                <SafeIcon icon={FiCreditCard} className="mr-2 text-primary/70" />
                <span>No credit card</span>
              </div>
            </div>
          </motion.div>
        </div>
        
        <motion.div 
          className="max-w-5xl mx-auto px-2 sm:px-4"
          initial={{opacity: 0, y: 40}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.8, delay: 1}}
        >
          <div className="relative">
            {/* Decorative elements - hidden on mobile */}
            <div className="hidden sm:block absolute -top-12 -left-12 w-16 sm:w-20 lg:w-24 h-16 sm:h-20 lg:h-24 bg-blue-100 rounded-full animate-pulse-slow"></div>
            <div className="hidden sm:block absolute -bottom-8 -right-8 w-12 sm:w-14 lg:w-16 h-12 sm:h-14 lg:h-16 bg-primary rounded-full animate-bounce-slow"></div>
            
            <div className="relative bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-2xl border border-gray-100">
              <div className="absolute -top-3 -right-3 sm:-top-4 sm:-right-4">
                <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white text-xs sm:text-sm font-bold px-3 sm:px-4 py-1 sm:py-2 rounded-full shadow-lg flex items-center">
                  <SafeIcon icon={FiZap} className="mr-1 sm:mr-2 text-xs sm:text-sm" />
                  <span className="hidden sm:inline">AI-Powered Platform</span>
                  <span className="sm:hidden">AI Platform</span>
                </div>
              </div>
              
              {/* YouTube Video Thumbnail with Play Button */}
              <div className="bg-bg-light rounded-xl sm:rounded-2xl overflow-hidden shadow-inner border border-gray-100 relative">
                <div 
                  className="aspect-video bg-gradient-to-br from-gray-900 to-gray-800 relative flex items-center justify-center group cursor-pointer" 
                  onClick={handleVideoClick}
                >
                  {/* YouTube Thumbnail Background */}
                  <div className="absolute inset-0">
                    <img 
                      src="https://img.youtube.com/vi/nFVsQ8Dp5gs/maxresdefault.jpg" 
                      alt="Yiksy Platform Demo"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    {/* Dark overlay for better contrast */}
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors"></div>
                  </div>
                  
                  <div className="text-center z-10">
                    <motion.div 
                      className="w-16 h-16 sm:w-20 sm:h-20 bg-primary/90 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl group-hover:bg-primary transition-colors border-2 border-white/20"
                      whileHover={{scale: 1.1}}
                      whileTap={{scale: 0.95}}
                    >
                      <SafeIcon icon={FiPlay} className="text-white text-2xl sm:text-3xl ml-1" />
                    </motion.div>
                    <p className="text-white font-medium text-sm sm:text-base drop-shadow-lg">Watch Platform Demo</p>
                  </div>
                  
                  {/* Fake UI elements */}
                  <div className="absolute bottom-4 left-4 right-4 bg-white/10 backdrop-blur-md p-3 rounded-lg border border-white/10 flex items-center justify-between hidden sm:flex">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                        <SafeIcon icon={FiZap} className="text-white" />
                      </div>
                      <span className="ml-3 font-medium text-sm text-white">Yiksy Dashboard Preview</span>
                    </div>
                    <div className="flex space-x-2">
                      <div className="w-12 h-2 bg-white/30 rounded-full"></div>
                      <div className="w-2 h-2 bg-white/30 rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Video Info */}
              <div className="mt-4 sm:mt-6 text-center">
                <p className="text-sm sm:text-base text-text-light">See how easy it is to create content in minutes</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default NewHero;